import { Switch as RadixSwitch } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getTipDefinition, type TipId } from '@/tips';
import { cn } from '@/utils/cn';

import { TipView } from './tip-view';

export const Switch = forwardRef<
  ElementRef<typeof RadixSwitch.Root>,
  ComponentPropsWithoutRef<typeof RadixSwitch.Root>
>(({ className, ...props }, ref) => (
  <RadixSwitch.Root
    ref={ref}
    className={cn(
      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
      'bg-border-strong data-[state=checked]:bg-brand',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <RadixSwitch.Thumb
      className={cn(
        'block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow-e1 transition-transform',
        'data-[state=checked]:translate-x-[1.375rem]',
      )}
    />
  </RadixSwitch.Root>
));

Switch.displayName = 'Switch';

/**
 * A switch with its label and explanation, for settings rows.
 *
 * The description is where the consequence goes — "gives leaders access to their
 * group's giving totals" — because a bare toggle label makes an administrator guess.
 * Prefer `tipId` when the guidance is a catalog tip.
 */
export const SwitchField = ({
  id,
  label,
  description,
  tipId,
  tip,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string;
  label: string;
  description?: string;
  tipId?: TipId;
  /** Extra node under the label (e.g. composed help). Prefer `tipId`. */
  tip?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('tips');
  const definition = tipId ? getTipDefinition(tipId) : undefined;
  if (tipId && !definition) {
    if (import.meta.env.DEV) {
      console.warn(`Tip "${tipId}" is missing from the tip catalog`);
    }
  }
  const tipMode = definition?.mode ?? null;
  const tipContent = tipId && definition ? t(tipId) : undefined;
  const descriptionId = `${id}-description`;

  const showInlineTip = tipId && (tipMode === 'inline' || tipMode === 'label') && tipContent;
  const showIconTip = tipId && tipMode === 'icon' && tipContent;
  const hasDescription = Boolean(description || showInlineTip || tip);

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <label htmlFor={id} className="block cursor-pointer text-body font-medium text-fg">
            {label}
          </label>
          {showIconTip ? (
            <TipView
              mode="icon"
              content={tipContent!}
              moreAboutLabel={t('moreAbout')}
            />
          ) : null}
        </div>
        {description ? (
          <p id={descriptionId} className="mt-0.5 text-caption text-fg-secondary">
            {description}
          </p>
        ) : null}
        {showInlineTip ? (
          <TipView
            mode="inline"
            content={tipContent!}
            moreAboutLabel={t('moreAbout')}
            id={description ? undefined : descriptionId}
            className="mt-0.5"
          />
        ) : null}
        {tip ? <div className="mt-0.5">{tip}</div> : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-describedby={hasDescription ? descriptionId : undefined}
      />
    </div>
  );
};
